/**
 * ROUND8: reading his trail (`world.hazards.tracks`, which hazards/tracks.ts now lays on every ground). A dog takes
 * the scent up by passing near a print, then runs it print by print toward the newest end - toward him.
 *
 * It names the print it is making for by the tick it was laid (`DogState.trailTick`), which no other print shares.
 * So when the trail is broken - wading wipes it, age or the save cap drops the print - the dog knows the line is
 * gone, instead of running on to whatever was laid next: after a river, that is on the far bank.
 */

import { secs } from '../../tuning';
import { DOGS } from '../../tuningDogs';
import type { MissionDef, Track, World } from '../../types';
import { waterBetween } from './dogNose';

/** Where in the trail the print laid on this tick is, or -1 when it has gone. */
export function printIndex(trail: readonly Track[], tick: number): number {
  // Laid oldest first, one tick apart at least: a binary search, though a trail is 96 prints at most.
  let lo = 0;
  let hi = trail.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = trail[mid]?.t ?? -1;
    if (t === tick) return mid;
    if (t < tick) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/** Still scent to a dog: laid inside `DOGS.trailSeconds`. */
export function freshPrint(p: Track, now: number): boolean {
  return now - p.t <= secs(DOGS.trailSeconds);
}

/**
 * The print a dog at (x, z) takes the scent up from: within `reach`, fresh, laid after `after` (the end of a line it
 * has already run to nothing), and not across water from it. `newest` picks the newest such print rather than the
 * nearest - for a dog that had him a moment ago, the newest is the way he went. Null when there is none.
 */
export function printNear(
  w: World, def: MissionDef, x: number, z: number, reach: number, after: number, newest = false,
): Track | null {
  let best: Track | null = null;
  let bestD = reach * reach;
  for (const p of w.hazards.tracks) {
    if (p.t <= after || !freshPrint(p, w.tick)) continue;
    const dx = p.x - x;
    const dz = p.z - z;
    const d = dx * dx + dz * dz;
    if (d > reach * reach) continue;
    if (newest ? best !== null && p.t <= best.t : d >= bestD) continue;
    if (waterBetween(def, x, z, p.x, p.z)) continue;
    best = p;
    bestD = d;
  }
  return best;
}
